import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import {
  Clock, Gavel, ListChecks, Gauge, Languages, TrendingUp, Users, Sparkles, CalendarRange,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const LANG_NAMES = {
  en: "English", hi: "Hindi", ta: "Tamil", te: "Telugu", kn: "Kannada",
  ml: "Malayalam", mr: "Marathi", bn: "Bengali", other: "Other", unknown: "Unknown",
};

const RANGES = [
  { w: 6, l: "6 weeks" },
  { w: 12, l: "12 weeks" },
  { w: 26, l: "26 weeks" },
];

function Stat({ label, value, unit, icon: Icon, sub, tone = "powder", testid }) {
  return (
    <div
      data-testid={testid}
      className="yl-surface yl-hover-lift yl-beam p-6 flex flex-col justify-between min-h-[148px]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="eyebrow leading-tight">{label}</div>
        <div className={`tone-${tone} w-9 h-9 rounded-xl grid place-items-center shrink-0`}>
          <Icon className="w-4 h-4" strokeWidth={1.9} />
        </div>
      </div>
      <div className="mt-5">
        <div className="font-heading text-4xl leading-none">
          {value}
          {unit && <span className="text-base text-muted-foreground font-sans font-semibold ml-1">{unit}</span>}
        </div>
        {sub && <div className="font-mono text-[10px] text-muted-foreground mt-2 uppercase tracking-wider">{sub}</div>}
      </div>
    </div>
  );
}

function Panel({ title, hint, icon: Icon, children, testid, className = "" }) {
  return (
    <section data-testid={testid} className={`yl-surface p-6 lg:p-7 ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg sm:text-xl flex items-center gap-2.5">
            {Icon && <Icon className="w-4 h-4 text-primary" strokeWidth={2} />}
            {title}
          </h2>
          {hint && <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>}
        </div>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

const tooltipStyle = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 14,
  fontSize: 12,
  color: "hsl(var(--popover-foreground))",
  boxShadow: "0 8px 30px hsl(var(--shadow-tint) / .12)",
};

export default function Insights() {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/ac/insights?weeks=${weeks}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [weeks]);

  if (loading && !data) {
    return (
      <div className="p-6 md:p-8 lg:p-12 space-y-8" data-testid="insights-loading">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-36 rounded-3xl" />)}
        </div>
        <Skeleton className="h-80 rounded-3xl" />
      </div>
    );
  }

  const t = data?.totals || {};
  const q = data?.quality || {};
  const trend = data?.trend || [];
  const health = q.avg_health ?? 0;

  return (
    <div data-testid="insights-root" className="p-6 md:p-8 lg:p-12 space-y-10 max-w-[1500px]">
      <header className="flex flex-wrap items-end justify-between gap-5">
        <div className="yl-fade-up">
          <div className="eyebrow">Analytics · workspace intelligence</div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-3 leading-none">Insights.</h1>
          <p className="text-sm md:text-base text-muted-foreground mt-3 max-w-xl leading-relaxed">
            How much time your meetings consume, how fast decisions move, and who is carrying the execution load.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="insights-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.w}
              type="button"
              data-testid={`insights-range-${r.w}`}
              onClick={() => setWeeks(r.w)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                weeks === r.w ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-foreground"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </header>

      {/* KPI strip */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat
          testid="insight-hours-saved" label="Hours saved" value={t.hours_saved ?? 0} unit="h"
          icon={Clock} tone="sage" sub={`${t.meeting_hours ?? 0}h of meetings recorded`}
        />
        <Stat
          testid="insight-decision-velocity" label="Decision velocity"
          value={data?.decision_velocity_days ?? "—"} unit={data?.decision_velocity_days ? "d" : ""}
          icon={TrendingUp} tone="powder" sub={`${t.decisions_approved ?? 0} of ${t.decisions ?? 0} approved`}
        />
        <Stat
          testid="insight-execution" label="Execution rate"
          value={t.tasks ? Math.round(((t.tasks_done || 0) / t.tasks) * 100) : 0} unit="%"
          icon={ListChecks} tone="lavender" sub={`${t.tasks_done ?? 0} of ${t.tasks ?? 0} tasks closed`}
        />
        <Stat
          testid="insight-health" label="Avg meeting health" value={q.avg_health ?? "—"}
          unit={q.avg_health ? "/100" : ""} icon={Gauge} tone="apricot"
          sub={`${q.meetings_analyzed ?? 0} meetings analysed`}
        />
      </section>

      {/* Trend */}
      <Panel
        testid="insights-trend" title="Meeting load & decision output" icon={CalendarRange}
        hint="Weekly meetings recorded against decisions captured — the ratio you want trending up."
      >
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={trend} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gMeet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gDec" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-2))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--chart-2))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="meetings" name="Meetings" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#gMeet)" />
              <Area type="monotone" dataKey="decisions" name="Decisions" stroke="hsl(var(--chart-2))" strokeWidth={2} fill="url(#gDec)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Panel
          testid="insights-throughput" title="Task throughput" icon={ListChecks}
          hint="Created vs closed each week." className="xl:col-span-2"
        >
          <div style={{ width: "100%", height: 250 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="created" name="Created" fill="hsl(var(--chart-3))" radius={[6, 6, 0, 0]} />
                <Bar dataKey="closed" name="Closed" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testid="insights-quality" title="Quality gauge" icon={Sparkles} hint="Average across analysed meetings.">
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer>
              <RadialBarChart
                innerRadius="68%" outerRadius="100%" barSize={14} startAngle={210} endAngle={-30}
                data={[{ name: "health", value: health }]}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" cornerRadius={99} fill="hsl(var(--chart-2))" background={{ fill: "hsl(var(--secondary))" }} />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
          <div className="-mt-20 text-center">
            <div className="font-heading text-4xl leading-none">{q.avg_health ?? "—"}</div>
            <div className="eyebrow text-[9px] mt-1.5">Meeting health</div>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-secondary/60 p-4">
              <div className="font-heading text-2xl leading-none">{q.avg_on_agenda ?? "—"}<span className="text-sm">%</span></div>
              <div className="eyebrow text-[9px] mt-1.5">On agenda</div>
            </div>
            <div className="rounded-2xl bg-secondary/60 p-4">
              <div className="font-heading text-2xl leading-none">{q.avg_transcript_quality ?? "—"}</div>
              <div className="eyebrow text-[9px] mt-1.5">Transcript</div>
            </div>
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Panel testid="insights-languages" title="Language mix" icon={Languages} hint="Share of validated speech by language.">
          {(data?.language_mix || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No analysed transcripts yet.</p>
          ) : (
            <div className="space-y-3.5">
              {(data?.language_mix || []).map((l, i) => (
                <div key={l.code} data-testid={`insight-lang-${l.code}`}>
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span>{LANG_NAMES[l.code] || l.code}</span>
                    <span className="font-mono text-muted-foreground">{l.share}%</span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-secondary overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${l.share}%`, background: `hsl(var(--chart-${(i % 5) + 1}))` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="insights-owners" title="Execution load" icon={Users} hint="Open, overdue and completed work per owner.">
          {(data?.owners || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No tasks assigned yet.</p>
          ) : (
            <ul className="divide-y divide-border -my-2">
              {(data?.owners || []).map((o) => (
                <li key={o.name} data-testid={`insight-owner-${o.name}`} className="py-3 flex items-center gap-4">
                  <span
                    className="w-8 h-8 rounded-xl grid place-items-center text-[10px] font-bold text-white shrink-0"
                    style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}
                  >
                    {(o.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <span className="text-sm font-semibold truncate flex-1">{o.name}</span>
                  <span className="shrink-0 flex items-center gap-1.5">
                    <span className="tone-powder rounded-full px-2.5 py-1 font-mono text-[10px] font-bold">{o.open} open</span>
                    {o.overdue > 0 && (
                      <span className="tone-blush rounded-full px-2.5 py-1 font-mono text-[10px] font-bold">{o.overdue} late</span>
                    )}
                    <span className="tone-sage rounded-full px-2.5 py-1 font-mono text-[10px] font-bold">{o.done} done</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel testid="insights-health-trend" title="Meeting health over time" icon={Gavel} hint="Weighted score per week — gaps mean no analysed meetings.">
        <div style={{ width: "100%", height: 230 }}>
          <ResponsiveContainer>
            <LineChart data={trend} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line
                type="monotone" dataKey="health" name="Health" stroke="hsl(var(--chart-2))" strokeWidth={2.5}
                dot={{ r: 3, fill: "hsl(var(--chart-2))" }} connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { l: "Meetings", v: t.meetings ?? 0 },
            { l: "Meeting hours", v: `${t.meeting_hours ?? 0}h` },
            { l: "Decisions", v: t.decisions ?? 0 },
            { l: "Tasks", v: t.tasks ?? 0 },
          ].map((k) => (
            <div key={k.l} className="rounded-2xl bg-secondary/50 p-4">
              <div className="font-heading text-2xl leading-none">{k.v}</div>
              <div className="eyebrow text-[9px] mt-1.5">{k.l}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
